import { useEffect, useRef } from "react";
import { findShortcut, SCOPES } from "./keyboardShortcutRegistry";

const usePOSKeyboard = ({
  posConfig,
  cart,
  tillLayout,
  // Modal/drawer togglers
  setSalesRepModalOpen,
  setSalesRepresentSearchInput,
  salesReprestiveList,
  setFilteredSalesRepresentList,
  setSelectedRowKeys,
  openPaymentModal,
  setDisplayOrderHistory,
  setDisplayParkedBillModal,
  setIsQtyUpdate,
  parkBill,
  confirmDiscardCart,
  closeProductPanel,
  setDisplayBatchSelection,
  closeCustomerSearch,
  closePaymentModal,
  // Cart operations
  increaseProductQty,
  decreaseProductQty,
  deleteProduct,
  // State refs (kept in sync via useEffect)
  selectedProductInCart,
  selectedRowKeys,
  paymentModal,
  selectedItemsModalOpen,
  // Setter for payment method on paymentModal open
  setSelectedPaymentMethod,
  tillDataPaymentMethods,
  // Zone navigation (optional — Phase 1 keyboard nav)
  zoneNavigation,
  layoutType,
  newShortcutHandlers,
}) => {
  // ── Internal refs to track latest values ────────────────────────────
  const cartItemsLengthRef = useRef(0);
  const selectedProductInCartRef = useRef({});
  const selectedRowKeysRef = useRef([0]);
  const paymentModalStateRef = useRef(false);

  useEffect(() => {
    cartItemsLengthRef.current = cart.items.length;
  }, [cart]);

  useEffect(() => {
    selectedProductInCartRef.current = selectedProductInCart;
  }, [selectedProductInCart]);

  useEffect(() => {
    selectedRowKeysRef.current = selectedRowKeys;
    if (selectedRowKeys.length > 0) {
      const cartIndex = cart.items.findIndex((ci) => ci.key === selectedRowKeys[0]);
      // setSelectedProductInCart({ ...cart.items[cartIndex] });
    }
  }, [selectedRowKeys]);

  useEffect(() => {
    paymentModalStateRef.current = paymentModal;
    if (paymentModal) {
      setSelectedPaymentMethod(tillDataPaymentMethods.filter((item, index) => item.isDefault === "Y")?.[0] || {});
    }
  }, [paymentModal]);

  // ── handleKey ─────────────────────────────────────────────────────────

  const handleKey = (e) => {
    const { altKey, keyCode } = e;
    const featureEnabled = posConfig?.enableKeyboardNavigation === "Y";

    if (featureEnabled && zoneNavigation) {
      const isFKey = keyCode >= 112 && keyCode <= 119;
      if (isFKey) {
        const handled = zoneNavigation.handleFKeyPress(keyCode, e);
        if (handled) return;
      }
      if (zoneNavigation.activeZoneId && zoneNavigation.handleInnerNavigation(e)) {
        return;
      }
    }

    if (altKey) {
      if (keyCode === 83) {
        if (posConfig?.showLineSalesRep === "Y") {
          setSalesRepModalOpen({ status: true, title: "itemSalesRep" });
          setSalesRepresentSearchInput("");
          setFilteredSalesRepresentList(salesReprestiveList);
        }
      }
      if (keyCode === 67) {
        setSelectedRowKeys((v) => {
          if (v.length > 0) {
            return [];
          } else {
            return [0];
          }
        });
      }
      if (keyCode === 80) {
        openPaymentModal();
      }

      if (keyCode === 79) {
        setDisplayOrderHistory(true);
      }

      if (keyCode === 66) {
        setDisplayParkedBillModal(true);
      }

      if (keyCode === 73) {
        if (selectedRowKeysRef.current.length > 0) {
          setIsQtyUpdate(selectedProductInCartRef.current);
        }
      }

      if (keyCode === 76) {
        parkBill("parkKey");
      }

      if (keyCode === 72) {
        confirmDiscardCart();
      }
    }

    if (keyCode === 115) {
      // F4 — handled by zone navigation when enableKeyboardNavigation === "Y"
    }
    if (keyCode === 27) {
      if ((e.target.id === "productSearchInputId" || e.target.id.search("productCardItem")) >= 0) {
        closeProductPanel();
      }
      setDisplayBatchSelection((b) => {
        if (b) {
          return !b;
        } else {
          return b;
        }
      });
      setSalesRepModalOpen({ status: false, title: "" });
      setSalesRepresentSearchInput("");
      closeCustomerSearch();

      if (paymentModalStateRef.current) {
        closePaymentModal();
      }
    }
    if (keyCode === 38) {
      setSelectedRowKeys((v) => {
        if (v.length > 0 && v[0] > 0) {
          return [v[0] - 1];
        } else {
          return [v[0]];
        }
      });
    }
    if (keyCode === 40) {
      setSelectedRowKeys((v) => {
        if (v.length > 0 && v[0] < cartItemsLengthRef.current - 1) {
          return [v[0] + 1];
        } else {
          return [v[0]];
        }
      });
    }

    if (keyCode === 187 || keyCode === 107) {
      if (selectedRowKeysRef?.current?.length > 0 && !selectedItemsModalOpen && cart.saleType?.value !== "DI") {
        increaseProductQty(selectedProductInCartRef.current);
      }
    }
    if (keyCode === 109 || keyCode === 189) {
      if (selectedRowKeysRef?.current?.length > 0 && !selectedItemsModalOpen && cart.saleType?.value !== "DI") {
        if (parseFloat(selectedProductInCartRef?.current?.weight) > 1) {
          decreaseProductQty(selectedProductInCartRef.current);
        }
      }
    }
    if (keyCode === 46) {
      if (selectedRowKeysRef?.current?.length > 0) {
        deleteProduct(selectedProductInCartRef.current);
      }
    }

    if (featureEnabled && keyCode === 27 && zoneNavigation?.activeZoneId) {
      zoneNavigation.clearZoneFocus();
    }

    if (featureEnabled && newShortcutHandlers) {
      const scope = layoutType === "restaurant" ? SCOPES.RESTAURANT : SCOPES.SUPERMARKET;
      const shortcut = findShortcut(e, scope, true);
      if (shortcut?.gated) {
        const handler = newShortcutHandlers[shortcut.action];
        if (handler) {
          e.preventDefault();
          handler(e, shortcut);
        }
      }
    }
  };

  // ── Wire up global keydown listener ───────────────────────────────────
  const handleKeyRef = useRef(handleKey);
  handleKeyRef.current = handleKey;

  useEffect(() => {
    const listener = (e) => handleKeyRef.current(e);
    window.addEventListener("keydown", listener);
    return () => {
      window.removeEventListener("keydown", listener);
    };
  }, []);

  // ── Return ────────────────────────────────────────────────────────────
  return {
    cartItemsLengthRef,
    selectedProductInCartRef,
    selectedRowKeysRef,
    paymentModalStateRef,
    handleKey,
  };
};

export default usePOSKeyboard;
